import { useEffect, useState } from 'react'
import type { MetadataConfig } from '@rhybus/metadata-management'
import { rhybusRListsHttp } from '../api/rhybus-list-http'
import { rhybusWhoHttp } from '../api/rhybus-who-http'
import { ListNames } from '../config/configuration'
import { db } from '../store/localbase'
import { TT_TT_TOOLTIP_ID, TT_TT_LABEL_NAME, TT_TT_DESCRIPTION, TOOLTIP_DEFAULTS } from './tooltip-ids'
import { convertDateTime } from '@rhybus/common-functions'
import { getContentAuditFields } from './contentManagement'

// ─── Tooltip ID map ───────────────────────────────────────────────────────────
/** Maps each Tooltip Metadata form field key to its ToolTipID. */
export const tooltipMetadataTooltipIds: Record<string, string> = {
    ToolTipID: TT_TT_TOOLTIP_ID,
    LabelName: TT_TT_LABEL_NAME,
    TooltipDescription: TT_TT_DESCRIPTION
}

// ============================================================
// TYPES
// ============================================================

export type MetadataManagementTabKey =
    | 'country'
    | 'exa'
    | 'facility-category'
    | 'implementing-agency'
    | 'location'
    | 'priority'
    | 'program-type'
    | 'rank'
    | 'training-type'
    | 'tooltips'
    | 'update-build-version'

export type MetadataDefinition = {
    key: MetadataManagementTabKey
    title: string
    metaDataType: string
}

export type CommonMetadataItem = {
    ID?: number
    Id?: number
    MetadataType: string
    MetadataValue: string
    MetadataCode?: string
    ParentCode?: string
    Code?: number
    ShortName?: string
    InternalCode?: number
    MetadataDescription?: string
    IsArchived?: boolean
    IsDeleted?: boolean
    AllowEdit?: boolean
    ItemModifiedBy?: { Title: string }
    ItemModified?: string
    Modified?: string
    eKey?: string
    eTag?: string
}

export type TooltipMetadataItem = {
    ID: number
    ToolTipID: string
    LabelName: string
    TooltipDescription: string
    ItemModified: string
}

export type ChecklistMetadataItem = {
    ID: number
    Id: number
    Title: string
    IsRequired: boolean | string
    IsArchived: boolean | string
    ItemModifiedBy?: { Title: string }
    ItemModified?: string
    Modified?: string
}

// ============================================================
// TABS
// ============================================================

export const metadataDefinitions: MetadataDefinition[] = [
    { key: 'country', title: 'Country', metaDataType: 'Country' },
    { key: 'exa', title: 'EXA', metaDataType: 'EXA' },
    { key: 'facility-category', title: 'Facility Category', metaDataType: 'FacilityCategory' },
    { key: 'implementing-agency', title: 'Implementing Agency', metaDataType: 'ImplementingAgency' },
    // { key: 'location', title: 'Location', metaDataType: 'Location' },
    { key: 'priority', title: 'Priority', metaDataType: 'Priority' },
    { key: 'program-type', title: 'Program Type', metaDataType: 'ProgramType' },
    { key: 'rank', title: 'Rank', metaDataType: 'Rank' },
    { key: 'training-type', title: 'Training Type', metaDataType: 'TrackType' },
]

export const metadataManagementTabs: Array<{ key: MetadataManagementTabKey; title: string }> = [
    ...metadataDefinitions.map(({ key, title }) => ({ key, title })),
    { key: 'tooltips', title: 'Tooltips' },
    { key: 'update-build-version', title: 'Update Build Version' },
]

// ============================================================
// SERVICE FUNCTIONS  (IndexedDB via existing getIndexedDBListData)
// ============================================================



export const getMetadataItemsByType = async (metaDataType: string): Promise<CommonMetadataItem[]> => {
    const list = await rhybusRListsHttp().getList(ListNames.COMMON_METADATA)
    const all = [...(await list.query().get<CommonMetadataItem>())]
    return all.filter((item: any) => {
        const type = item.MetadataType ?? item.MetaDataType
        return type === metaDataType && !item.IsDeleted
    })
}

const refreshMetadataCache = async (_metaDataType: string): Promise<void> => undefined

const toBooleanSP = (val: any): boolean => val === true || val === 'Yes'

const toNumericCode = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value)
        if (Number.isFinite(parsed)) return parsed
    }
    return undefined
}

const getNextMetadataCode = async (): Promise<number> => {
    const items = [...(await (await rhybusRListsHttp().getList(ListNames.COMMON_METADATA)).query().get<any>())]
    const maxCode = items.reduce((max, item) => {
        const itemCode = toNumericCode(item?.Code ?? item?.InternalCode)
        return itemCode !== undefined ? Math.max(max, itemCode) : max
    }, 0)
    return maxCode + 1
}

const resolveMetadataCode = async (data: Partial<CommonMetadataItem>): Promise<number> => {
    const existingCode = toNumericCode(data.Code ?? data.InternalCode)
    if (existingCode !== undefined) return existingCode
    return getNextMetadataCode()
}

const sanitizeForSP = (data: any): any => {
    const result = { ...data }
    if ('IsArchived' in result) result.IsArchived = toBooleanSP(result.IsArchived)
    if ('AllowEdit' in result) result.AllowEdit = toBooleanSP(result.AllowEdit)
    return result
}

export const saveMetadataItem = async (
    data: Partial<CommonMetadataItem>,
    metaDataType: string
): Promise<void> => {
    const me = await rhybusWhoHttp().Me()
    const userId = me.displayName ?? me.persona ?? me.email
    const now = new Date().toISOString()
    const code = await resolveMetadataCode(data)
    await (await rhybusRListsHttp().getList(ListNames.COMMON_METADATA)).addOrUpdateItem(sanitizeForSP(
        {
            ...data,
            MetadataType: metaDataType,
            Code: String(code),
            IsArchived: false,
            IsDeleted: false,
            ItemCreatedBy: userId,
            ItemModifiedBy: userId,
            ItemCreated: now,
            ItemModified: now,
        }) as any)
    await refreshMetadataCache(metaDataType)
}

export const editMetadataItem = async (
    data: Partial<CommonMetadataItem>,
    metaDataType: string
): Promise<void> => {
    const { eKey, eTag } = data as any
    const { ID: _id, Id: _Id, Modified: _mod, ItemModifiedBy: _imb, ...cleanData } = data as any
    const code = await resolveMetadataCode(cleanData)
    const me = await rhybusWhoHttp().Me()
    const userId = me.displayName ?? me.persona ?? me.email
    const now = new Date().toISOString()
    await (await rhybusRListsHttp().getList(ListNames.COMMON_METADATA)).addOrUpdateItem(sanitizeForSP({
        ...cleanData,
        MetadataType: cleanData.MetadataType ?? metaDataType,
        Code: String(code),
        IsDeleted: false,
        ItemModified: now,
        ItemModifiedBy: userId,
        eKey,
        eTag,
    }) as any)
    await refreshMetadataCache(metaDataType)
}

export const removeMetadataItem = async (
    data: Partial<CommonMetadataItem>,
    metaDataType: string
): Promise<void> => {
    const { eKey, eTag } = data as any
    const { ID: _id, Id: _Id, Modified: _mod, ItemModifiedBy: _imb, ...cleanData } = data as any
    const me = await rhybusWhoHttp().Me()
    const userId = me.profileName
    const now = new Date().toISOString()
    await (await rhybusRListsHttp().getList(ListNames.COMMON_METADATA)).addOrUpdateItem(sanitizeForSP({
        ...cleanData,
        MetadataType: cleanData.MetadataType ?? metaDataType,
        IsDeleted: true,
        ItemModified: now,
        ItemModifiedBy: userId,
        eKey,
        eTag,
    }) as any)
    await refreshMetadataCache(metaDataType)
}

export const updateBuildVersionData = async (): Promise<void> => {
    // Clear all local caches
    localStorage.clear()
    sessionStorage.clear()
    await db.delete()

    // Increment or create build version in SP
    const serverBuildVersion = [...(await (await rhybusRListsHttp().getList(ListNames.BUILD_MODIFIED_LIST)).query().get<any>())]
    if (serverBuildVersion?.length > 0) {
        await (await rhybusRListsHttp().getList(ListNames.BUILD_MODIFIED_LIST)).addOrUpdateItem({
            eKey: String(serverBuildVersion[0].ID),
            BuildVersion: (serverBuildVersion[0].BuildVersion ?? 0) + 1
        } as any)
    } else {
        await (await rhybusRListsHttp().getList(ListNames.BUILD_MODIFIED_LIST)).addOrUpdateItem({
            BuildVersion: 1
        } as any)
    }
}

// ============================================================
// DISPLAY MAPPER  (used in settings.tsx useMemo)
// ============================================================

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const resolveMetadataModifiedByNames = async (items: any[]): Promise<any[]> => {
    const guids = [...new Set(
        items.map(i => i.ItemModifiedBy).filter((v): v is string => typeof v === 'string' && UUID_REGEX.test(v))
    )]
    if (guids.length === 0) return items
    const resolvedMap = new Map<string, string>()
    await Promise.all(guids.map(async (guid) => {
        try {
            const user = await rhybusWhoHttp().OtherByUserId(guid)
            resolvedMap.set(guid, (user as any)?.displayName ?? (user as any)?.profileName ?? (user as any)?.email ?? guid)
        } catch {
            resolvedMap.set(guid, guid)
        }
    }))
    return items.map(item => ({
        ...item,
        ItemModifiedBy: typeof item.ItemModifiedBy === 'string' && resolvedMap.has(item.ItemModifiedBy)
            ? resolvedMap.get(item.ItemModifiedBy)
            : item.ItemModifiedBy,
    }))
}

export const mapCommonMetadataForDisplay = (items: any[]): any[] =>
    items.map((item: any) => ({
        ...item,
        ID: item.ID ?? item._Id ?? item.eKey,
        Id: item._Id ?? item.ID ?? item.eKey,
        MetadataValue: item.MetadataValue ?? item.MetaDataValue,
        MetadataType: item.MetadataType ?? item.MetaDataType,
        IsArchived: item.IsArchived ? 'Yes' : 'No',
        Modified: `${item.ItemModifiedBy ?? ''} | ${convertDateTime(item.ItemModified)}`,
    }))

// ============================================================
// FORM OPTIONS HOOK  (create-form.tsx dynamic selects)
// ============================================================

type SelectOption = { label: string; value: string }

export const getMetadataOptions = async (metaDataType: string): Promise<SelectOption[]> => {
    const items = await getMetadataItemsByType(metaDataType)
    return items
        .filter((item: any) => !item.IsArchived)
        .map((item: any) => {
            const val = item.MetadataValue ?? item.MetaDataValue ?? ''
            return { label: val, value: val }
        })
        .filter(opt => opt.label !== '')
}

export function useFormMetadataOptions() {

    const [isLoading, setIsLoading] = useState(true)
    const [countryOptions, setCountryOptions] = useState<SelectOption[]>([])
    const [implementingAgencyOptions, setImplementingAgencyOptions] = useState<SelectOption[]>([])
    const [programTypeOptions, setProgramTypeOptions] = useState<SelectOption[]>([])
    const [fundingSourceOptions, setFundingSourceOptions] = useState<SelectOption[]>([])
    const [programTypeFundingSourceMap, setProgramTypeFundingSourceMap] = useState<Record<string, string>>({})
    const [programTypeCodeMap, setProgramTypeCodeMap] = useState<Record<string, number>>({})
    const [regionalCenterItems, setRegionalCenterItems] = useState<CommonMetadataItem[]>([])
    const [priorityOptions, setPriorityOptions] = useState<SelectOption[]>([])
    const [rankOptions, setRankOptions] = useState<SelectOption[]>([])
    const [trackTypeOptions, setTrackTypeOptions] = useState<SelectOption[]>([])
    const [trainingEnrollmentStatusOptions, setTrainingEnrollmentStatusOptions] = useState<SelectOption[]>([])
    const [financialStatusOptions, setFinancialStatusOptions] = useState<SelectOption[]>([])
    const [facilityCategoryOptions, setFacilityCategoryOptions] = useState<SelectOption[]>([])
    const [wcnOptions, setWcnOptions] = useState<SelectOption[]>([])
    const [exaOptions, setExaOptions] = useState<SelectOption[]>([])
    const [suffixOptions, setSuffixOptions] = useState<SelectOption[]>([])
    const [quarterOptions, setQuarterOptions] = useState<SelectOption[]>([])

    useEffect(() => {
        const load = async () => {
            const list = await rhybusRListsHttp().getList(ListNames.COMMON_METADATA)
            const all = [...(await list.query().get<CommonMetadataItem>())]

            // single-pass helpers — avoids repeated full-list fetches
            const active = (type: string) =>
                all.filter((item: any) =>
                    (item.MetadataType ?? item.MetaDataType) === type &&
                    !item.IsDeleted && !item.IsArchived
                )
            const toValueOpt = (item: CommonMetadataItem): SelectOption => {
                const val = item.MetadataValue ?? ''
                return { label: val, value: val }
            }
            const toCodeLabelOption = (s: CommonMetadataItem): SelectOption => {
                const val = s.MetadataValue ?? ''
                const desc = s.MetadataDescription ? ` - ${s.MetadataDescription}` : ''
                return { label: `${val}${desc}`, value: val }
            }

            const countries = active('Country')
            if (countries.length) setCountryOptions(countries.map(toValueOpt).filter(o => o.label !== ''))

            const agencies = active('ImplementingAgency')
            if (agencies.length)
                setImplementingAgencyOptions(
                    agencies.map(toCodeLabelOption).filter(o => o.value !== '')
                )

            setRegionalCenterItems(active('RegionalCenter'))

            const programTypes = active('ProgramType')
            const fundingSources = active('FundingSource')

            setProgramTypeOptions(
                programTypes
                    .map(pt => {
                        const val = pt.MetadataValue ?? ''
                        const desc = pt.MetadataDescription ? ` - ${pt.MetadataDescription}` : ''
                        return { label: `${val}${desc}`, value: val }
                    })
                    .filter(o => o.value !== '')
            )
            setFundingSourceOptions(
                fundingSources
                    .map(toValueOpt)
                    .filter(o => o.label !== '')
                    .filter((opt, idx, arr) => arr.findIndex(o => o.value === opt.value) === idx)
            )
            setPriorityOptions(active('Priority').map(toValueOpt).filter(o => o.label !== ''))
            setRankOptions(active('Rank').map(toValueOpt).filter(o => o.label !== ''))
            setTrackTypeOptions(active('TrackType').map(toCodeLabelOption).filter(o => o.value !== ''))
            setTrainingEnrollmentStatusOptions(active('TrainingStatus').map(toCodeLabelOption).filter(o => o.value !== ''))
            setFinancialStatusOptions(active('FinancialStatus').map(toCodeLabelOption).filter(o => o.value !== ''))
            setFacilityCategoryOptions(active('FacilityCategory').map(toCodeLabelOption).filter(o => o.value !== ''))
            setWcnOptions(active('WCN').map(toValueOpt).filter(o => o.label !== ''))
            setExaOptions(active('EXA').map(toValueOpt).filter(o => o.label !== ''))
            setSuffixOptions(active('Suffix').map(toValueOpt).filter(o => o.label !== ''))
            setQuarterOptions(active('Quarter').map(toValueOpt).filter(o => o.label !== ''))

            // programType value → Code (used to match RegionalCenter ParentCode)
            const codeMap: Record<string, number> = {}
            programTypes
                .filter(pt => pt.Code != null)
                .forEach(pt => { if (pt.MetadataValue && pt.Code != null) codeMap[pt.MetadataValue] = pt.Code! })
            setProgramTypeCodeMap(codeMap)

            // programType value → funding source value
            const ptCodeToValue = new Map(
                programTypes.filter(pt => pt.Code != null).map(pt => [String(pt.Code), pt.MetadataValue ?? ''])
            )
            const fsMap: Record<string, string> = {}
            fundingSources
                .filter(fs => fs.ParentCode != null)
                .forEach(fs => {
                    const ptValue = ptCodeToValue.get(String(fs.ParentCode))
                    if (ptValue && fs.MetadataValue) fsMap[ptValue] = fs.MetadataValue
                })
            if (Object.keys(fsMap).length) setProgramTypeFundingSourceMap(fsMap)
        }
        load().finally(() => setIsLoading(false))
    }, [])

    return { isLoading, countryOptions, implementingAgencyOptions, programTypeOptions, fundingSourceOptions, programTypeFundingSourceMap, programTypeCodeMap, regionalCenterItems, priorityOptions, rankOptions, trackTypeOptions, trainingEnrollmentStatusOptions, financialStatusOptions, facilityCategoryOptions, wcnOptions, exaOptions, suffixOptions, quarterOptions }
}

// Resolves metadata options for a set of form-engine Select fields at runtime.
// Pass an array of { id, metaDataType } pairs (from the published schema).
// Returns a map of fieldId → SelectOption[] that updates once options are fetched.
export function useMetadataOptionsMap(
    pairs: { id: string; metaDataType: string }[]
): Record<string, SelectOption[]> {
    const [optionsMap, setOptionsMap] = useState<Record<string, SelectOption[]>>({})

    useEffect(() => {
        if (pairs.length === 0) return
        const uniqueTypes = [...new Set(pairs.map(p => p.metaDataType))]
        Promise.all(uniqueTypes.map(t => getMetadataOptions(t).then(opts => ({ t, opts }))))
            .then(results => {
                const byType: Record<string, SelectOption[]> = {}
                for (const { t, opts } of results) byType[t] = opts
                const map: Record<string, SelectOption[]> = {}
                for (const { id, metaDataType } of pairs) map[id] = byType[metaDataType] ?? []
                setOptionsMap(map)
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pairs.map(p => `${p.id}:${p.metaDataType}`).join('|')])

    return optionsMap
}

// ============================================================
// METADATA CONFIGS — Branch / Division / Product / TBM / BuildVersion
// ============================================================

const makeCommonMetadataConfig = (entityName: string, fieldLabel: string): MetadataConfig => ({
    entityName,
    layout: 'normal',
    list: {
        title: entityName,
        subtitle: `Add ${entityName}`,
        fields: [
            { key: 'MetadataValue', label: entityName, className: 'col' },
            { key: 'Modified', label: 'Modified', className: 'col-md-4' },
            { key: 'IsArchived', label: 'Is Archived', className: 'col-md-2' },
        ],
        noResultText: `No ${entityName} metadata available.`,
    },
    form: {
        fields: [
            {
                key: 'MetadataValue',
                type: 'text',
                label: fieldLabel,
                placeholder: `Enter ${fieldLabel}`,
                required: true,
                layoutcolumns: 'col-12 col-lg-12',
                formclassName: 'form-vertical',
                isInfo: true,
                info: fieldLabel,
                infoIcon: 'icon-rb-info',
            },
            {
                key: 'IsArchived',
                type: 'select' as any,
                label: 'Is Archived',
                options: ['Yes', 'No'],
                visibleIn: 'edit' as any,
                layoutcolumns: 'col-12 col-lg-6',
                formclassName: 'form-vertical',
                isInfo: true,
                info: 'Is Archived',
                infoIcon: 'icon-rb-info',
            },
        ],
    },
    buttons: {
        add: { label: 'Add', icon: 'icon-rb-add' },
        save: { label: 'Save', icon: 'icon-rb-save' },
        edit: { label: 'Edit', icon: 'icon-rb-pencil', variant: 'secondary', size: 'sm' },
        update: { label: 'Update', icon: 'icon-rb-arrow-repeat' },
        delete: { label: 'Delete', icon: 'icon-rb-trashbin', variant: 'secondary', size: 'sm' },
        cancel: { label: 'Cancel', icon: 'icon-rb-close', variant: 'secondary' },
    },
    messages: {
        noChange: `No ${entityName} changes detected`,
        confirmDelete: `Are you sure you want to delete this ${entityName} item?`,
        empty: `No ${entityName} metadata available.`,
    },
})

export const branchConfig = makeCommonMetadataConfig('Branch', 'Branch')
export const divisionConfig = makeCommonMetadataConfig('Division', 'Division')
export const productConfig = makeCommonMetadataConfig('Product', 'Product')
export const tbmConfig = makeCommonMetadataConfig('TBM', 'TBM')
export const buildVersionConfig = makeCommonMetadataConfig('Update Build Version', 'Build Version')

// ============================================================
// TOOLTIP CONFIG
// ============================================================

export const tooltipMetadataConfig: MetadataConfig = {
    entityName: 'Tooltip',
    layout: 'accordion',
    list: {
        title: 'Tooltips',
        subtitle: 'Tooltips',
        fields: [
            { key: 'ToolTipID', label: 'Tooltip ID', className: 'col' },
            { key: 'LabelName', label: 'Tooltip Label', className: 'col-md-3' },
            { key: 'Modified', label: 'Modified', className: 'col-md-2' },
        ],
        noResultText: 'No tooltip metadata is available.',
    },
    accordion: {
        sections: [
            { key: 'TooltipDescription', label: 'Tooltip Description' },
        ],
    },
    form: {
        fields: [
            {
                key: 'ToolTipID', type: 'text', label: 'Tooltip ID',
                readOnly: true, visibleIn: 'edit',
                layoutcolumns: 'col-12 col-lg-6', formclassName: 'form-vertical',
                isInfo: true, info: 'Tooltip ID', infoIcon: 'icon-rb-info',
            },
            {
                key: 'LabelName', type: 'text', label: 'Tooltip Label',
                readOnly: true, visibleIn: 'edit',
                layoutcolumns: 'col-12 col-lg-6', formclassName: 'form-vertical',
                isInfo: true, info: 'Tooltip Label', infoIcon: 'icon-rb-info',
            },
            {
                key: 'TooltipDescription', type: 'textarea', label: 'Tooltip Description',
                placeholder: 'Update tooltip description', required: true, maxLength: 2000,
                layoutcolumns: 'col-12', formclassName: 'form-vertical',
                isInfo: true, info: 'Tooltip Description', infoIcon: 'icon-rb-info',
            },
        ],
    },
    buttons: {
        edit: { label: 'Edit', icon: 'icon-rb-pencil', variant: 'secondary', size: 'sm' },
        update: { label: 'Update', icon: 'icon-rb-arrow-repeat' },
        cancel: { label: 'Cancel', icon: 'icon-rb-close', variant: 'secondary' },
    },
    messages: {
        noChange: 'No tooltip description changes detected',
        empty: 'No tooltip metadata is available.',
    },
}

// ============================================================
// CHECKLIST SERVICE FUNCTIONS
// ============================================================

export const getChecklistItems = async (): Promise<ChecklistMetadataItem[]> =>
    [...(await (await rhybusRListsHttp().getList(ListNames.CHECKLIST)).query().get<any>())] as ChecklistMetadataItem[]

export const saveChecklistItem = async (data: Partial<ChecklistMetadataItem>): Promise<void> => {
    await (await rhybusRListsHttp().getList(ListNames.CHECKLIST)).addOrUpdateItem({ ...data, IsRequired: toBooleanSP(data.IsRequired), IsArchived: toBooleanSP(data.IsArchived) } as any)
}

export const editChecklistItem = async (data: Partial<ChecklistMetadataItem>): Promise<void> => {
    await (await rhybusRListsHttp().getList(ListNames.CHECKLIST)).addOrUpdateItem({ ...data, IsRequired: toBooleanSP(data.IsRequired), IsArchived: toBooleanSP(data.IsArchived) } as any)
}

export const removeChecklistItem = async (data: Partial<ChecklistMetadataItem>): Promise<void> => {
    await (await rhybusRListsHttp().getList(ListNames.CHECKLIST)).deleteItem((data as any).eKey, (data as any).eTag)
}

export const mapChecklistForDisplay = (items: ChecklistMetadataItem[]): any[] =>
    items.map(item => ({
        ...item,
        IsRequired: item.IsRequired === true || item.IsRequired === 'Yes' ? 'Yes' : 'No',
        IsArchived: item.IsArchived === true || item.IsArchived === 'Yes' ? 'Yes' : 'No',
        Modified: `${item.ItemModifiedBy?.Title ?? ''} | ${convertDateTime(item.ItemModified)}`,
    }))

// ============================================================
// CHECKLIST CONFIG
// ============================================================

export const checklistConfig: MetadataConfig = {
    entityName: 'Checklist',
    layout: 'normal',
    list: {
        title: 'Checklist',
        subtitle: 'Add Checklist Item',
        fields: [
            { key: 'Title', label: 'Checklist Item', className: 'col' },
            { key: 'Modified', label: 'Modified', className: 'col-md-4' },
            { key: 'IsRequired', label: 'Is Required', className: 'col-md-2' },
            { key: 'IsArchived', label: 'Is Archived', className: 'col-md-2' },
        ],
        noResultText: 'No checklist items available.',
    },
    form: {
        fields: [
            {
                key: 'Title',
                type: 'text',
                label: 'Checklist Item',
                placeholder: 'Enter checklist item',
                required: true,
                layoutcolumns: 'col-12 col-lg-12',
                formclassName: 'form-vertical',
                isInfo: true,
                info: 'Checklist Item',
                infoIcon: 'icon-rb-info',
            },
            {
                key: 'IsRequired',
                type: 'select' as any,
                label: 'Is Required',
                options: ['Yes', 'No'],
                layoutcolumns: 'col-12 col-lg-6',
                formclassName: 'form-vertical',
                isInfo: true,
                info: 'Is Required',
                infoIcon: 'icon-rb-info',
            },
            {
                key: 'IsArchived',
                type: 'select' as any,
                label: 'Is Archived',
                options: ['Yes', 'No'],
                visibleIn: 'edit' as any,
                layoutcolumns: 'col-12 col-lg-6',
                formclassName: 'form-vertical',
                isInfo: true,
                info: 'Is Archived',
                infoIcon: 'icon-rb-info',
            },
        ],
    },
    buttons: {
        add: { label: 'Add', icon: 'icon-rb-add' },
        save: { label: 'Save', icon: 'icon-rb-save' },
        edit: { label: 'Edit', icon: 'icon-rb-pencil', variant: 'secondary', size: 'sm' },
        update: { label: 'Update', icon: 'icon-rb-arrow-repeat' },
        delete: { label: 'Delete', icon: 'icon-rb-trashbin', variant: 'secondary', size: 'sm' },
        cancel: { label: 'Cancel', icon: 'icon-rb-close', variant: 'secondary' },
    },
    messages: {
        noChange: 'No checklist changes detected',
        confirmDelete: 'Are you sure you want to delete this checklist item?',
        empty: 'No checklist items available.',
    },
}


export const getToolTipItems = async (): Promise<TooltipMetadataItem[]> =>
    [...(await (await rhybusRListsHttp().getList(ListNames.DSCA_TOOLTIP)).query().get<any>())] as TooltipMetadataItem[]

export const editTooltipItem = async (data: Partial<TooltipMetadataItem>): Promise<void> => {
    const auditFields = await getContentAuditFields()
    const { ID, ToolTipID, LabelName, TooltipDescription, eKey, eTag } = data as any
    const payload: any = { TooltipID: ToolTipID, TooltipName: LabelName, TooltipDescription, eKey, eTag, ...auditFields }
    if (ID !== undefined && ID !== null) payload.ID = ID
    await (await rhybusRListsHttp().getList(ListNames.DSCA_TOOLTIP)).addOrUpdateItem(payload)
}

/**
 * Creates a ToolTipList entry for every tooltip ID in TOOLTIP_DEFAULTS
 * that is not already present in `existingItems`.  Safe to call repeatedly —
 * it skips IDs that already have an entry.  Returns the number of entries created.
 */
export const seedMissingTooltips = async (existingItems: TooltipMetadataItem[]): Promise<number> => {
    const existingIds = new Set(existingItems.map((item: any) => item.TooltipID))
    const missing = Object.entries(TOOLTIP_DEFAULTS).filter(([id]) => !existingIds.has(id))
    if (missing.length === 0) return 0
    for (const [tooltipId, description] of missing) {
        await editTooltipItem({
            ToolTipID: tooltipId,
            LabelName: tooltipId,
            TooltipDescription: description,
        } as any)
    }
    return missing.length
}
